import { markdown } from "@codemirror/lang-markdown";
import { EditorView } from "@codemirror/view";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { compose, type Problem } from "./compose";
import { editorMenu } from "./editor-menu";
import { generaAncore } from "./ancore";
import { fraseDaCursore } from "./ancoraggio";
import { impronta } from "./impronta";
import { marca, type Marcatura } from "./markup";
import { redo, undo } from "@codemirror/commands";
import { autocompletion } from "@codemirror/autocomplete";
import { openSearchPanel } from "@codemirror/search";

import { sostituisciTutto, vaiA } from "./documento";
import { applicaMeta, leggiMeta } from "./meta";
import { leggiParagrafi } from "./paragrafi";
import { suggerimenti } from "./suggerimenti";
import { canonicalizzaTabelle } from "./tabelle";
import { tabelleAutomatiche } from "./tabelle-auto";
import { Paragrafi } from "./components/Paragrafi";
import { applicaTema, temaEffettivo, temaSalvato, type Tema } from "./tema";
import { Metadati } from "./components/Metadati";
import { apri, nativoDisponibile, nomePdf, salvaCome, salvaSuHandle, scarica } from "./files";
import { PdfView, type Comandi } from "./components/PdfView";
import { ESEMPIO } from "./esempio";


const MEMORIA = "md2pdf-webui:sorgente";

// Sotto questa larghezza i due pannelli non ci stanno: se ne mostra uno solo.
// La soglia e' la stessa del CSS — sta scritta due volte perche' una la deve
// sapere il layout e una la logica, ma il numero e' uno.
const STRETTO = "(max-width: 900px)";


export function App(): React.JSX.Element
{
    const [source, setSource] = useState(() => localStorage.getItem(MEMORIA) ?? ESEMPIO);
    const [pdf, setPdf] = useState<Uint8Array<ArrayBuffer> | null>(null);
    const [problems, setProblems] = useState<Problem[]>([]);
    // L'esito e' un DATO, non una stringa da annusare: la prima stesura
    // decideva se mostrare il pannello rosso guardando se il log conteneva la
    // parola «non» — cioe' una frase innocente col «non» dentro finiva in
    // rosso, e un rifiuto senza «non» spariva.
    const [esito, setEsito] = useState<{ ok: boolean; testo: string }>({ ok: true, testo: "" });
    const [componendo, setComponendo] = useState(false);
    const [improntaPdf, setImprontaPdf] = useState<string | null>(null);
    const [salto, setSalto] = useState("");
    const anteprima = useRef<Comandi>(null);

    // Il menù contestuale non è React: legge questo quando lo apri, così dice
    // lo stato di ADESSO invece di quello del render in cui è stato creato.
    const statoPdf = useRef<{ presente: boolean; stantio: boolean }>({ presente: false, stantio: false });
    const editor = useRef<ReactCodeMirrorRef>(null);
    const [vista, setVista] = useState<"sorgente" | "anteprima">("sorgente");
    const [nome, setNome] = useState("documento.md");
    const [metadati, setMetadati] = useState(false);
    const [paragrafi, setParagrafi] = useState(false);
    const [anteprimaVisibile, setAnteprimaVisibile] = useState(true);
    const [tema, setTema] = useState<Tema>(temaSalvato);
    const [handle, setHandle] = useState<FileSystemFileHandle | null>(null);
    const [sporco, setSporco] = useState(false);
    const nativo = nativoDisponibile();

    // Il sorgente sopravvive al ricaricamento: uno strumento che ti fa
    // ricominciare da capo a ogni F5 non lo usi due volte.
    useEffect(() =>
    {
        localStorage.setItem(MEMORIA, source);
    }, [source]);

    useEffect(() => applicaTema(tema), [tema]);

    // Chiudere la pagina con modifiche non salvate su un FILE e' una perdita
    // vera: il localStorage tiene il testo, ma il file su disco resta indietro
    // e nessuno lo dice. Il browser chiede conferma solo se c'e' qualcosa da
    // perdere davvero.
    useEffect(() =>
    {
        if (!sporco || handle === null) { return; }

        const chiedi = (e: BeforeUnloadEvent): void => { e.preventDefault(); };
        window.addEventListener("beforeunload", chiedi);
        return () => window.removeEventListener("beforeunload", chiedi);
    }, [sporco, handle]);

    const apriFile = useCallback(async (): Promise<void> =>
    {
        const aperto = await apri();
        if (aperto === null) { return; }

        setSource(aperto.testo);
        setNome(aperto.nome);
        setHandle(aperto.handle);
        setSporco(false);
        setVista("sorgente");
    }, []);

    // `Salva` riscrive il file aperto se il browser ce lo lascia fare; se non
    // c'e' un file (o non c'e' l'API) diventa un «salva come», che almeno non
    // finge di aver scritto dove non ha scritto.
    const salva = useCallback(async (): Promise<void> =>
    {
        if (handle !== null)
        {
            await salvaSuHandle(handle, source);
            setSporco(false);
            return;
        }

        const nuovo = await salvaCome(nome, source);
        if (nuovo !== null) { setHandle(nuovo); setNome(nuovo.name); }
        setSporco(false);
    }, [handle, nome, source]);

    const componi = useCallback(async (): Promise<void> =>
    {
        setComponendo(true);

        // Le tabelle si allineano da sole prima di comporre: il PDF verrebbe
        // identico comunque, ma il sorgente resta leggibile a chi lo scrive.
        // Si fa QUI e non a ogni tasto perché riscrivere una tabella mentre la
        // stai digitando ti sposterebbe il testo sotto le dita.
        const vista = editor.current?.view;
        const testo = vista ? vista.state.doc.toString() : source;
        const allineato = canonicalizzaTabelle(testo);

        if (vista && allineato !== testo) { sostituisciTutto(vista, allineato); }

        const risposta = await compose(allineato);
        if (risposta.ok)
        {
            setPdf(risposta.pdf);
            setImprontaPdf(impronta(allineato));
            setProblems([]);
            setEsito({ ok: true, testo: risposta.log });

            // Su uno schermo stretto hai premuto «Componi» per VEDERE il PDF:
            // restare sull'editor ti costringerebbe a un secondo tocco per
            // avere quello che avevi chiesto.
            if (window.matchMedia(STRETTO).matches) { setVista("anteprima"); }
        }
        else
        {
            // Il PDF di prima RESTA a schermo: e' l'ultima cosa che ha
            // funzionato, e toglierla non aiuta nessuno a capire l'errore.
            setProblems(risposta.problems);
            setEsito({ ok: false, testo: risposta.output });

            // Il verso opposto: se non si compone, il posto giusto e' il
            // sorgente — e' li' che si ripara.
            if (window.matchMedia(STRETTO).matches) { setVista("sorgente"); }
        }
        setComponendo(false);
    }, [source]);

    // Ctrl+Invio: la scorciatoia che tutti provano per prima.
    useEffect(() =>
    {
        const onKey = (e: KeyboardEvent): void =>
        {
            if (!(e.ctrlKey || e.metaKey)) { return; }

            if (e.key === "Enter")
            {
                e.preventDefault();
                void componi();
            }
            if (e.key.toLowerCase() === "s")
            {
                // Ctrl+S in una pagina web salva la PAGINA: qui e' l'ultima
                // cosa che vuoi, e la scorciatoia va rubata al browser.
                e.preventDefault();
                void salva();
            }
            if (e.key.toLowerCase() === "o")
            {
                e.preventDefault();
                void apriFile();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [componi]);

    // Vecchia o no, l'anteprima lo dice da sé: si confronta l'impronta del
    // sorgente di adesso con quella del sorgente che ha prodotto il PDF.
    // Le tabelle però si allineano in composizione, quindi il confronto va
    // fatto sul testo canonicalizzato: altrimenti il PDF risulterebbe stantio
    // per una spaziatura che il PDF non ha mai visto diversa.
    const stantio = useMemo(
        () => pdf !== null && improntaPdf !== null && impronta(canonicalizzaTabelle(source)) !== improntaPdf,
        [source, improntaPdf, pdf],
    );


    // «Portami nel PDF dove sono adesso». Non esiste una mappa riga→pagina —
    // pandoc scrive LaTeX e xelatex impagina — quindi si cerca nel PDF la
    // frase che hai sotto il cursore. Ha senso solo se il PDF è quello del
    // testo di adesso: su un'anteprima vecchia porterebbe in un punto che nel
    // frattempo si è spostato, ed è il tipo di bugia peggiore, quella
    // plausibile. Per questo il bottone è spento quando è stantia.
    const portaIlPdfQui = useCallback(async (): Promise<void> =>
    {
        const view = editor.current?.view;

        if (!view) { return; }

        const frase = fraseDaCursore(view.state.doc.toString(), view.state.selection.main.head);

        if (frase === null)
        {
            setSalto("qui non c'è testo da cercare nel PDF");
            return;
        }

        const esitoSalto = await anteprima.current?.vaiAlTesto(frase);

        setSalto(esitoSalto === "trovato"
            ? ""
            : esitoSalto === "non-pronto"
                ? "l'anteprima non è ancora pronta"
                : `«${frase}» non si trova nel PDF`);

        if (window.matchMedia(STRETTO).matches) { setVista("anteprima"); }
    }, []);


    statoPdf.current = { presente: pdf !== null, stantio };


    // Il pannello di ricerca e' quello di CodeMirror (stesse scorciatoie che
    // uno gia' conosce): i due bottoni differiscono per il campo su cui
    // atterra il fuoco, perche' «trova» e «sostituisci» sono lo stesso
    // pannello ma due intenzioni diverse.
    const cerca = useCallback((conSostituzione: boolean): void =>
    {
        const view = editor.current?.view;

        if (!view) { return; }

        openSearchPanel(view);

        const campo = view.dom.querySelector<HTMLInputElement>(
            conSostituzione ? '.cm-search input[name="replace"]' : '.cm-search input[name="search"]',
        );

        campo?.focus();
        campo?.select();
    }, []);


    const estensioni = useMemo(
        () => [
            markdown(),
            // Le righe vanno a capo da sole invece di scorrere in orizzontale.
            // È l'a capo VISIVO: nel file la riga resta una riga sola, quindi
            // il markdown non cambia e il PDF nemmeno. Segue la larghezza
            // dell'editor da sé — anche quando nascondi l'anteprima o giri il
            // telefono — perché è CodeMirror a rifare il calcolo, non noi.
            EditorView.lineWrapping,
            // I suggerimenti li mettiamo noi e basta: quelli di serie del
            // markdown non conoscono i § e si contenderebbero il popup.
            autocompletion({ override: [suggerimenti] }),
            editorMenu({
                componi: () => void componi(),
                vaiNelPdf: () => void portaIlPdfQui(),
                perchePdfNo: () => (!statoPdf.current.presente
                    ? "non c'è ancora un'anteprima: premi «Componi»"
                    : statoPdf.current.stantio
                        ? "l'anteprima non è aggiornata: ricomponi, poi ti ci porto"
                        : undefined),
            }),
            tabelleAutomatiche(),
        ],
        [componi, portaIlPdfQui],
    );

    // Il numero di riga negli errori di md2pdf non e' decorazione: si clicca
    // e il cursore ci va.
    const vaiAllaRiga = (line: number): void =>
    {
        setVista("sorgente");

        const view = editor.current?.view;
        if (!view) { return; }

        const riga = view.state.doc.line(Math.min(line, view.state.doc.lines));
        view.dispatch({ selection: { anchor: riga.from }, scrollIntoView: true });
        view.focus();
    };

    return (
        <div className="app">
            <header>
                <h1>md2pdf</h1>
                <span className="file" title={nativo ? "il salvataggio riscrive questo file" : "senza secure context il salvataggio SCARICA una copia"}>
                    {nome}{sporco ? " •" : ""}
                    {!nativo && <span className="fioco"> (scarica)</span>}
                </span>

                <button type="button" onClick={() => void apriFile()}>Apri</button>
                <button type="button" onClick={() => void salva()}>Salva</button>
                <button
                    type="button"
                    className="solo-largo-inline"
                    onClick={() => void salvaCome(nome, source).then((h) => { if (h) { setHandle(h); setNome(h.name); setSporco(false); } })}
                >
                    Salva come…
                </button>
                <button
                    type="button"
                    disabled={pdf === null}
                    onClick={() => { if (pdf !== null) { scarica(new Blob([pdf], { type: "application/pdf" }), nomePdf(nome)); } }}
                >
                    Scarica PDF
                </button>
                <span className="spazio" />
                {esito.ok && esito.testo !== "" && <span className="esito">{esito.testo}</span>}

                <div className="scelta solo-stretto" role="group" aria-label="Cosa mostrare">
                    <button
                        type="button"
                        aria-pressed={vista === "sorgente"}
                        onClick={() => setVista("sorgente")}
                    >
                        Sorgente
                    </button>
                    <button
                        type="button"
                        aria-pressed={vista === "anteprima"}
                        onClick={() => setVista("anteprima")}
                    >
                        Anteprima
                    </button>
                </div>

                {pdf !== null && (
                    <span className={stantio ? "stato stantio" : "stato fresco"} role="status">
                        {stantio ? "anteprima non aggiornata" : "anteprima aggiornata"}
                    </span>
                )}

                <button
                    type="button"
                    disabled={pdf === null || stantio}
                    title={pdf === null
                        ? "prima componi l'anteprima"
                        : stantio
                            ? "l'anteprima non è aggiornata: ricomponi, poi ti ci porto"
                            : "porta l'anteprima dove sei nel testo"}
                    onClick={() => void portaIlPdfQui()}
                >
                    Vai qui nel PDF
                </button>

                <button type="button" className="primario" onClick={() => void componi()} disabled={componendo}>
                    {componendo ? "compongo…" : "Componi"}
                    <span className="solo-largo"> ⌃↵</span>
                </button>
            </header>

            <div className="strumenti">
                {([
                    ["grassetto", "B", "Grassetto"],
                    ["corsivo", "I", "Corsivo"],
                    ["sottolineato", "U", "Sottolineato"],
                    ["sbarrato", "S", "Sbarrato"],
                ] as [Marcatura, string, string][]).map(([quale, segno, titolo]) => (
                    <button
                        key={quale}
                        type="button"
                        className={`segno segno-${quale}`}
                        title={titolo}
                        aria-label={titolo}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { const v = editor.current?.view; if (v) { marca(v, quale); } }}
                    >
                        {segno}
                    </button>
                ))}

                <span className="divisore" />

                <button
                    type="button"
                    title="Annulla (⌃Z)"
                    aria-label="Annulla"
                    onClick={() => { const v = editor.current?.view; if (v) { undo(v); v.focus(); } }}
                >
                    ↶
                </button>
                <button
                    type="button"
                    title="Ripristina (⌃⇧Z)"
                    aria-label="Ripristina"
                    onClick={() => { const v = editor.current?.view; if (v) { redo(v); v.focus(); } }}
                >
                    ↷
                </button>

                <span className="divisore" />

                <button type="button" onClick={() => cerca(false)}>Trova</button>
                <button type="button" className="solo-largo-inline" onClick={() => cerca(true)}>
                    Trova e sostituisci
                </button>

                <span className="divisore" />

                <button type="button" onClick={() => setParagrafi(true)}>Paragrafi</button>
                <button
                    type="button"
                    title="Dà un §id ai titoli # e ## che non ce l'hanno, derivandolo dal titolo"
                    onClick={() =>
                    {
                        const view = editor.current?.view;

                        if (!view) { return; }

                        const esitoAncore = generaAncore(view.state.doc.toString());

                        sostituisciTutto(view, esitoAncore.testo);
                        setSalto(esitoAncore.aggiunte.length === 0
                            ? "tutti i capitoli hanno già un'ancora"
                            : `${esitoAncore.aggiunte.length} ancore aggiunte: ${esitoAncore.aggiunte.map((a) => `§${a.id}`).join(", ")}`);
                    }}
                >
                    Genera ancore
                </button>
                <button type="button" onClick={() => setMetadati(true)}>Metadati</button>
                <span className="spazio" />

                <button
                    type="button"
                    className="solo-largo"
                    aria-pressed={!anteprimaVisibile}
                    onClick={() => setAnteprimaVisibile(!anteprimaVisibile)}
                >
                    {anteprimaVisibile ? "Nascondi anteprima PDF" : "Mostra anteprima PDF"}
                </button>

                <div className="scelta" role="group" aria-label="Tema">
                    {([["sistema", "Auto"], ["chiaro", "Chiaro"], ["scuro", "Scuro"]] as [Tema, string][])
                        .map(([quale, etichetta]) => (
                            <button
                                key={quale}
                                type="button"
                                aria-pressed={tema === quale}
                                onClick={() => setTema(quale)}
                            >
                                {etichetta}
                            </button>
                        ))}
                </div>
            </div>

            <main data-vista={vista} data-anteprima={anteprimaVisibile ? "sì" : "no"}>
                <section className="sorgente">
                    <CodeMirror
                        ref={editor}
                        value={source}
                        height="100%"
                        theme={temaEffettivo(tema) === "scuro" ? "dark" : "light"}
                        extensions={estensioni}
                        onChange={(testo) => { setSource(testo); setSporco(true); }}
                        basicSetup={{
                            lineNumbers: true,
                            foldGutter: false,
                            highlightActiveLine: true,
                            // Selezionare una parola non deve accendere ogni
                            // altra occorrenza nel documento: in un manuale
                            // pieno di termini ripetuti e' rumore, non aiuto.
                            highlightSelectionMatches: false,
                            autocompletion: false,
                        }}
                    />
                </section>

                <section className="anteprima" data-stantio={stantio ? "sì" : "no"}>
                    {stantio && (
                        <p className="avviso-stantio">
                            Il documento è cambiato dopo questa composizione — premi «Componi» (⌃↵) per aggiornarla.
                        </p>
                    )}
                    <PdfView pdf={pdf} ref={anteprima} />
                </section>
            </main>

            {salto !== "" && (
                <p className="nota-salto" role="status" onAnimationEnd={() => setSalto("")}>{salto}</p>
            )}

            {metadati && (
                <Metadati
                    iniziali={leggiMeta(source)}
                    chiudi={() => setMetadati(false)}
                    applica={(voci) =>
                    {
                        const v = editor.current?.view;
                        if (v) { sostituisciTutto(v, applicaMeta(source, voci)); }
                        setMetadati(false);
                    }}
                />
            )}

            {paragrafi && (
                <Paragrafi
                    paragrafi={leggiParagrafi(source)}
                    chiudi={() => setParagrafi(false)}
                    vai={(p) =>
                    {
                        const v = editor.current?.view;
                        setParagrafi(false);
                        if (v) { vaiA(v, p.da); }
                    }}
                />
            )}

            {!esito.ok && (
                <footer className="problemi">
                    <strong>
                        {problems.length > 0 ? "Il sorgente non è componibile" : "La composizione non è partita"}
                    </strong>
                    {problems.length > 0
                        ? (
                            <ul>
                                {problems.map((p, i) => (
                                    <li key={i}>
                                        <button type="button" className="riga" onClick={() => vaiAllaRiga(p.line)}>
                                            riga {p.line}
                                        </button>
                                        <span>{p.message}</span>
                                    </li>
                                ))}
                            </ul>
                        )
                        : <pre>{esito.testo}</pre>}
                </footer>
            )}
        </div>
    );
}
